import React, { Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import {
    FloatingFocusManager,
    FloatingPortal,
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useMergeRefs,
    useRole,
} from '@floating-ui/react';
import { cx } from './classNames';
import useOpenState from './useOpenState';

// MUI Popover/Menu paper in the app theme.
export const floatingPaperClassName = 'z-[1300] rounded-lg border border-solid border-ui-line bg-ui-surface '
    + 'text-ui-ink shadow-[0_8px_24px_rgba(12,23,38,0.12)] outline-none';

/**
 * Where a focus-taking floating panel (Menu, Popover) renders: in the
 * nearest dialog around its trigger, else <body> (undefined). A modal dialog
 * (an MUI Dialog's focus trap) pulls focus back from anything outside it,
 * so a panel opened from inside one must render inside it. The panels use
 * fixed positioning, so the dialog's scrolling does not clip them.
 */
export function floatingRootFor(reference) {
    return reference?.closest?.('[role="dialog"]') ?? undefined;
}

/**
 * A panel that opens when `trigger` is clicked, in the app's popover paper.
 * `trigger` is one element that takes a ref; it gets `aria-haspopup`,
 * `aria-expanded` and `aria-controls`. The panel is a non-modal dialog
 * (name it with `aria-label` or `aria-labelledby`): focus moves into it,
 * and Escape, an outside click or tabbing out closes it, returning focus to
 * the trigger. `children` is its content, or a function of `close`. Inside
 * a dialog it renders in that dialog (see floatingRootFor).
 *
 * Open state is kept here unless `open` is given; `onOpenChange(open,
 * event, reason)` reports every request either way.
 *
 * Instead of a trigger, `anchor` places the panel against an element
 * rendered elsewhere (a header, a chip): the caller opens it through `open`
 * and keeps the anchor's own aria-expanded. `modal` traps focus inside the
 * panel and hides the rest of the page from assistive technology, as an MUI
 * Popover does; Tab then cycles inside it, and a press in anything opened
 * over it afterwards (an MUI Dialog it opened) leaves it open.
 * `returnFocus={false}` leaves focus where the caller puts it on close;
 * `offset` is the gap to the anchor in px.
 */
export default function Popover({
    trigger = null,
    anchor = null,
    open: openProp,
    onOpenChange,
    placement = 'bottom-start',
    offset: gap = 4,
    modal = false,
    returnFocus = true,
    className,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    children,
}) {
    const [open, setOpen] = useOpenState(openProp, onOpenChange);
    const {
        refs,
        elements,
        floatingStyles,
        context,
    } = useFloating({
        open,
        onOpenChange: setOpen,
        placement,
        strategy: 'fixed',
        elements: anchor ? { reference: anchor } : undefined,
        whileElementsMounted: autoUpdate,
        middleware: [offset(gap), flip(), shift({ padding: 8 })],
    });
    const click = useClick(context, { enabled: Boolean(trigger) });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'dialog' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
    const anchorElement = trigger ? Children.only(trigger) : null;
    const anchorRef = useMergeRefs([refs.setReference, anchorElement?.ref]);
    const close = () => setOpen(false);
    return (
        <>
            {anchorElement && cloneElement(
                anchorElement,
                getReferenceProps({ ...anchorElement.props, ref: anchorRef }),
            )}
            {open && (
                <FloatingPortal root={floatingRootFor(elements.domReference)}>
                    <FloatingFocusManager context={context} modal={modal} returnFocus={returnFocus}>
                        <div
                            ref={refs.setFloating}
                            style={floatingStyles}
                            aria-label={ariaLabel}
                            aria-labelledby={ariaLabelledBy}
                            className={cx(floatingPaperClassName, className)}
                            {...getFloatingProps()}
                        >
                            {typeof children === 'function' ? children(close) : children}
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
}

Popover.propTypes = {
    trigger: PropTypes.element,
    anchor: PropTypes.object,
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    placement: PropTypes.string,
    offset: PropTypes.number,
    modal: PropTypes.bool,
    returnFocus: PropTypes.bool,
    className: PropTypes.string,
    'aria-label': PropTypes.string,
    'aria-labelledby': PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};
